import { Component, useEffect, useState, type ReactNode } from 'react';
import type { DockviewPanelApi, IDockviewPanelProps } from 'dockview-react';
import { PanelVisibilityContext } from './panel-visibility.js';
import { ScreenerPanel } from './panels/screener.js';
import { CompanyOverviewPanel } from './panels/company-overview.js';
import { HomesPanel } from './panels/homes.js';
import { HomeRecordPanel } from './panels/home-record.js';
import { IdentityPanel } from './panels/identity.js';
import { FinancialSnapshotPanel } from './panels/financial-snapshot.js';
import { WatchlistPanel } from './panels/watchlist.js';
import { CompoundSignalAlertsPanel } from './panels/landing-alerts.js';
import { WatchlistChangesPanel } from './panels/landing-watchlist-changes.js';
import { DemandShiftsV2Panel } from './panels/demand-shifts.js';
import { OperatingPressurePanel } from './panels/operating-pressure.js';
import { MarketOpportunityPanel } from './panels/market-opportunity.js';
import { RollupSimulatorPanel } from './panels/rollup-simulator.js';
import { PlatformBuilderPanel } from './panels/platform-builder.js';
import { AlertsPanel } from './panels/alerts.js';
import { OriginationSignalPanel } from './panels/origination-signal.js';
import { ReimbursementPanel } from './panels/reimbursement.js';
import { MapPanel } from './panels/map.js';
import { BoltOnPanel } from './panels/bolt-on.js';
import { ConsolidatorsPanel } from './panels/consolidators.js';
import { OwnershipNetworkPanel } from './panels/ownership-network.js';
import { GroupOverviewPanel } from './panels/group-overview.js';
import { CensusPanel } from './panels/census.js';
import { MethodologyPanel } from './panels/methodology.js';
import { SourcesPanel } from './panels/sources.js';
import { MandatePanel } from './panels/mandate.js';
import { PortfolioPanel } from './panels/portfolio.js';
import { HelpPanel } from './panels/help.js';
import { CoveragePanel } from './panels/coverage.js';
import type { PanelId, TabId } from './store.js';

// Dockview's component-registration contract: a map from component-key to a
// React component that takes IDockviewPanelProps. We pass our store's
// (tabId, panelId) through the `params` field of each panel we add via the
// dockview API, then unwrap them here so the panel components stay pure.
//
// Adding a new panel component is one entry in this map plus an addPanel()
// call in the layout seeder.

export interface PanelParams {
  tabId: TabId;
  panelId: PanelId;
  /** Optional opaque initial-state bag passed through to the panel at
   *  mount time. Lets callers spawn pre-configured panels (e.g. a
   *  Screener pinned to the "quarantined-only" filter) without
   *  touching the workspace store schema. Persists across reloads
   *  because dockview's saved layout serialises panel params. */
  initialState?: Record<string, unknown>;
}

// Track a dockview panel's visibility (active tab vs hidden background tab)
// reactively, so wrapped panels can defer work until they're on screen.
function useDockviewVisibility(api: DockviewPanelApi): boolean {
  const [visible, setVisible] = useState(api.isVisible);
  useEffect(() => {
    setVisible(api.isVisible);
    const sub = api.onDidVisibilityChange((e) => setVisible(e.isVisible));
    return () => sub.dispose();
  }, [api]);
  return visible;
}

// Null render for RETIRED panel-component keys — keeps dockview from
// crashing on a persisted layout that still references a removed panel,
// until the store migration / backend-sync strip cleans the record.
function RetiredPanel(_props: PanelParams) {
  return null;
}

function wrap<P extends PanelParams>(PanelComponent: React.ComponentType<P>) {
  return function WrappedPanel(props: IDockviewPanelProps<PanelParams>) {
    // Defensive: if dockview rehydrates a saved panel without params
    // (shouldn't happen, but persisted state from older builds CAN
    // surprise us), fall back to extracting tabId/panelId from the
    // dockview panel api so the component still has its essentials.
    const params: PanelParams = props.params ?? {
      tabId: '' as PanelParams['tabId'],
      panelId: props.api.id as PanelParams['panelId'],
    };
    const visible = useDockviewVisibility(props.api);
    // PanelErrorBoundary localises crashes so one broken panel can't
    // take down the entire workspace tab. The error gets surfaced
    // inside the panel chrome instead of bubbling up to the React
    // root and unmounting everything.
    return (
      <PanelVisibilityContext.Provider value={visible}>
        <PanelErrorBoundary
          panelId={String(params.panelId)}
          component={props.api.component as string}
        >
          <PanelComponent {...(params as unknown as P)} />
        </PanelErrorBoundary>
      </PanelVisibilityContext.Provider>
    );
  };
}

/** Error boundary scoped to a single panel. Catches any error thrown
 *  during render / hooks of the wrapped panel and renders a fallback
 *  with the panel id + component key so the user can identify and
 *  close the bad panel without losing the rest of their workspace. */
class PanelErrorBoundary extends Component<
  { panelId: string; component: string; children: ReactNode },
  { error: Error | null }
> {
  override state: { error: Error | null } = { error: null };

  static getDerivedStateFromError(error: Error): { error: Error } {
    return { error };
  }

  override componentDidCatch(error: Error, info: { componentStack?: string | null }): void {
    // eslint-disable-next-line no-console
    console.error(
      `[hc] panel ${this.props.panelId} (${this.props.component}) crashed`,
      error,
      info.componentStack,
    );
  }

  override render(): ReactNode {
    if (!this.state.error) return this.props.children;
    return (
      <div
        role="alert"
        style={{
          padding: 'var(--s-4)',
          fontSize: 'var(--t-body)',
          color: 'var(--text-primary)',
          background: 'var(--surface)',
          height: '100%',
          overflow: 'auto',
          display: 'flex',
          flexDirection: 'column',
          gap: 'var(--s-2)',
        }}
      >
        <strong style={{ color: 'var(--alert)' }}>This panel crashed.</strong>
        <div style={{ fontSize: 'var(--t-meta)', color: 'var(--text-tertiary)' }}>
          {this.props.component} · {this.props.panelId}
        </div>
        <pre
          style={{
            margin: 0,
            padding: 'var(--s-2)',
            background: 'var(--surface-elevated)',
            border: '1px solid var(--hairline)',
            borderRadius: 2,
            fontSize: 'var(--t-meta)',
            fontFamily: 'var(--font-mono)',
            whiteSpace: 'pre-wrap',
            overflow: 'auto',
            maxHeight: 200,
          }}
        >
          {this.state.error.message}
        </pre>
        <div style={{ fontSize: 'var(--t-meta)', color: 'var(--text-tertiary)' }}>
          Close this panel from its ⋯ menu and re-open it from ⌘K. If the same panel keeps crashing
          on every open, file the error message above.
        </div>
      </div>
    );
  }
}

export const panelComponents = {
  // Per interface-brief.md Section 2.3 + 7: Screener and Company
  // Overview are the canonical Workspace-tab panels. The Target list
  // is gone — replaced by multiple named watchlists. The legacy
  // 'target-list' / 'target-list-funnel' keys are aliased to the
  // Watchlist panel so persisted-layout state from earlier builds still
  // opens a working panel.
  screener: wrap(ScreenerPanel),
  'company-overview': wrap(CompanyOverviewPanel),
  homes: wrap(HomesPanel),
  'home-record': wrap(HomeRecordPanel),
  watchlist: wrap(WatchlistPanel),
  alerts: wrap(AlertsPanel),
  'target-list-funnel': wrap(WatchlistPanel),
  'target-list': wrap(WatchlistPanel),
  'landing-alerts': wrap(CompoundSignalAlertsPanel),
  'landing-watchlist-changes': wrap(WatchlistChangesPanel),
  // 'landing-buyer' (the M&A Activity Tracker) was RETIRED 2026-07-11 —
  // deal tracking is off-thesis (the platform originates pre-deal). The key
  // stays registered as a null render so any persisted layout that still
  // references it doesn't crash dockview before the store migration /
  // backend-sync strip removes the record.
  'landing-buyer': wrap(RetiredPanel),
  'landing-pressure': wrap(OperatingPressurePanel),
  'market-opportunity': wrap(MarketOpportunityPanel),
  'rollup-simulator': wrap(RollupSimulatorPanel),
  'platform-builder': wrap(PlatformBuilderPanel),
  'origination-signal': wrap(OriginationSignalPanel),
  reimbursement: wrap(ReimbursementPanel),
  map: wrap(MapPanel),
  'bolt-on': wrap(BoltOnPanel),
  consolidators: wrap(ConsolidatorsPanel),
  'ownership-network': wrap(OwnershipNetworkPanel),
  'group-overview': wrap(GroupOverviewPanel),
  census: wrap(CensusPanel),
  coverage: wrap(CoveragePanel),
  methodology: wrap(MethodologyPanel),
  sources: wrap(SourcesPanel),
  mandate: wrap(MandatePanel),
  portfolio: wrap(PortfolioPanel),
  // 'landing-demand' was the news-agent Demand-side stub — RETIRED; the
  // rebuilt statistical panel below is 'demand-shifts'. Key kept as a null
  // render for stale persisted layouts.
  'landing-demand': wrap(RetiredPanel),
  'demand-shifts': wrap(DemandShiftsV2Panel),
  help: wrap(HelpPanel),
  identity: wrap(IdentityPanel),
  'financial-snapshot': wrap(FinancialSnapshotPanel),
} as const;

export type PanelComponentKey = keyof typeof panelComponents;
